/**
 * 읽기 전용 DSD-IXD 대사 섹션 — 목차(chunk) 하나의 DSD 패널 + 매핑된 IXD 패널 + DSD→IXD 포커스.
 *
 * 라이브 비교 화면과 *같은* 컴포넌트·훅을 쓴다(vanilla 재구현이 아님). 두 화면이 쓴다:
 * 대사 다운로드 HTML 의 standalone 뷰어(`ixd-dsd-compare/export/viewer`)와 주석 레퍼런스 검색의
 * XBRL 매핑 보기. 그래서 화면 폴더(`components/ixd-dsd-compare`) 밖의 공용 단위다.
 * `IxdDsdCompareStoreContext` 안에서 렌더해야 한다(캐러셀 activeIxdChunkId 를 스토어에서 읽는다).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type {
  DSDSubChunkInfo,
  DsdHeaderTag,
  FsType,
  IxdChunk,
} from "@/apis/compare/route";
import { useNavigation } from "@/hooks/ixd-dsd-compare/use-navigation";
import { useKeyboardShortcuts } from "@/hooks/ixd-dsd-compare/use-keyboard-shortcuts";
import { useDsdIxdCounterpart } from "@/hooks/ixd-dsd-compare/use-dsd-ixd-counterpart";
import { useIxdDsdCounterpart } from "@/hooks/ixd-dsd-compare/use-ixd-dsd-counterpart";
import type { CellIdentityLocation } from "@/utils/ixd-cell-model";
import type { LinkageIndex } from "@/components/ixd-dsd-compare/export/linkage-index";
import { collectMatchedIxdCellIds } from "@/utils/ixd-dsd-compare/build-ixd-by-dsd-cell-id";
import {
  PreviewPanel,
  type PreviewPanelHandle,
} from "@/components/ixd-dsd-compare/preview-panel";
import type {
  DsdDlRegionView,
  DsdLineItemRegionView,
  DsdMemberRegionView,
  DsdPeriodRegionView,
} from "@/components/ixd-dsd-compare/dsd-preview-renderer";
import IxdPreviewPanel from "@/components/ixd-dsd-compare/ixd-preview-panel";
import { DsdRegionReviewToggle } from "@/components/ixd-dsd-compare/region-review-toggle";
import { buildMemberAxisOptions } from "@/components/ixd-dsd-compare/dsd-region-views";
import type { IxdPreviewRendererRef } from "@/components/ixd-preview";
import { MatchNavigationPopover } from "@/components/ixd-dsd-compare/match-navigation-popover";
import { useIxdDsdCompareStore } from "@/stores/ixd-dsd-compare";
import {
  FOCUS_FAILED_MESSAGE,
  focusWithRetry,
} from "@/utils/ixd-dsd-compare/focus-with-retry";

// 공용 단위는 app/ 을 import 하지 않는다 — 네비 상태 타입은 그 훅의 파라미터 타입에서 얻는다.
type MatchNavigation = NonNullable<
  Parameters<typeof useNavigation>[0]["matchNavigation"]
>;

const NOOP = () => {};
const EMPTY_CELL_CHANGES: Record<string, string> = {};
const EMPTY_IDENTITY_CHANGES = new Map<string, string>();
// chunkFsTypeById 는 편집 호출(read-only 에선 미발생) 전용이라 빈 맵으로 충분.
const EMPTY_FS_TYPE_BY_ID = new Map<string, FsType>();

export interface ReadonlyCompareSectionData {
  id: string;
  title: string;
  dsdSubChunks: DSDSubChunkInfo[];
  /** 라이브 IXD 패널과 동일한 매핑 IXD 청크(detail.ixdChunks). */
  ixdChunks: IxdChunk[];
  /** DSD 헤더 태그(헤더 클릭 → IXD 헤더 포커스 / 헤더 태그 팝오버용). */
  dsdHeaderTags?: DsdHeaderTag[];
  /** DSD 셀의 DL 배정 영역. 클릭 포커싱의 DL 제약과 배정 검토 표시가 이걸 쓴다. */
  dlRegions?: DsdDlRegionView[] | null;
  /** DSD 셀의 기간 배정 영역. 같은 cell_id 의 당기/전기 구분에 쓴다. */
  periodRegions?: DsdPeriodRegionView[] | null;
  lineItemRegions?: DsdLineItemRegionView[] | null;
  memberRegions?: DsdMemberRegionView[] | null;
}

interface ReadonlyCompareSectionProps {
  section: ReadonlyCompareSectionData;
  /** 연동(same-value) 인덱스 — 보고서 전체 기준(라이브 동일). */
  identityIndex: LinkageIndex;
  /** IXD editor preview 라벨 맵(헤더 태그 한글 라벨). */
  ixdLabelMap: Map<string, string>;
  /** IXD 연동 위치 클릭 처리(다른 섹션이면 전환 후 포커스). */
  onFocusLocation: (location: CellIdentityLocation) => boolean;
  /** 다른 섹션에서 전환되어 들어올 때 마운트 후 포커스할 위치. */
  pendingFocus: { current: CellIdentityLocation | null };
  /**
   * DL·기간·항목·멤버 배정 검토 스위치를 패널 위에 항상 보인다(beta 게이트 없음).
   * 추론된 배정을 보는 것이 목적인 화면(주석 레퍼런스 검색)이 켠다. 다운로드 뷰어는 끔.
   */
  regionReview?: boolean;
}

/** 한 목차 섹션: DSD/IXD 패널 + 라이브와 동일한 DSD→IXD 포커스 / IXD 연동 팝오버 배선. */
export function ReadonlyCompareSection({
  section,
  identityIndex,
  ixdLabelMap,
  onFocusLocation,
  pendingFocus,
  regionReview = false,
}: ReadonlyCompareSectionProps) {
  const ixdRendererRef = useRef<IxdPreviewRendererRef>(null);
  const previewPanelRef = useRef<PreviewPanelHandle | null>(null);
  const [matchNavigation, setMatchNavigation] =
    useState<MatchNavigation | null>(null);
  const activeIxdChunkId = useIxdDsdCompareStore((s) => s.activeIxdChunkId);
  const setActiveIxdChunkId = useIxdDsdCompareStore(
    (s) => s.setActiveIxdChunkId,
  );
  // ── IXD ↔ DSD 셀 선택 대응(스크롤 + 강조). 라이브와 같은 훅을 쓴다 ──
  // 뷰어는 편집 모드가 없어 `disabled` 를 주지 않는다.
  const { onIxdCellIdsChange, counterpartDsdIds } = useIxdDsdCounterpart({
    subChunks: section.dsdSubChunks,
    previewPanelRef,
    resetKey: activeIxdChunkId,
  });

  const { onDsdCellIdsChange, ixdCounterpart } = useDsdIxdCounterpart({
    subChunks: section.dsdSubChunks,
    ixdRendererRef,
    resetKey: activeIxdChunkId,
  });

  // 매칭 DSD 없는 IXD 셀 빨강 표시용. 라이브 CompareContentView 와 동일 계산.
  const matchedIxdCellIds = useMemo(
    () => collectMatchedIxdCellIds(section.dsdSubChunks),
    [section.dsdSubChunks],
  );

  // 섹션 진입 시 IXD 캐러셀을 이 섹션의 첫 IXD 청크로 초기화(이전 섹션 잔상 방지).
  useEffect(() => {
    setActiveIxdChunkId(section.ixdChunks[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeIxdChunk = useMemo(
    () =>
      section.ixdChunks.find((c) => c.id === activeIxdChunkId) ??
      section.ixdChunks[0] ??
      null,
    [section.ixdChunks, activeIxdChunkId],
  );

  const navigation = useNavigation({
    ixdRendererRef,
    matchNavigation,
    onMatchNavigationChange: setMatchNavigation,
    currentIxdChunks: section.ixdChunks,
    activeIxdChunkId: activeIxdChunkId ?? undefined,
    onActiveIxdChunkChange: (chunk) => setActiveIxdChunkId(chunk.id),
  });

  // IXD 셀 선택 시 IXD 포커스 정리 + DSD 클릭 focus(ring/popover) 리셋 (라이브 handleCompareFocusClose 동일).
  const [focusedDsdResetKey, setFocusedDsdResetKey] = useState(0);
  const handleClearDsdFocus = useCallback(() => {
    navigation.handleNavigationClose();
    setFocusedDsdResetKey((key) => key + 1);
  }, [navigation]);

  // 라이브 페이지가 걸던 단축키 배선(Shift+방향키 매칭 이동, ESC 닫기).
  useKeyboardShortcuts({
    matchNavigation,
    onNavigatePrev: navigation.handleNavigatePrev,
    onNavigateNext: navigation.handleNavigateNext,
    onNavigateClose: handleClearDsdFocus,
  });

  // DSD 헤더 태그(헤더 클릭 → IXD 헤더 포커스 / 헤더 태그 팝오버). 라이브 동일.
  const headerTagsByDsdId = useMemo(() => {
    const tags = section.dsdHeaderTags;
    if (!tags?.length) return null;
    return new Map(tags.map((t) => [t.dsdId, t]));
  }, [section.dsdHeaderTags]);

  // 헤더 태그의 lineItem/member 를 한글 라벨로 채운다(라이브 동일). 라벨 없으면 raw 유지.
  const labeledHeaderTags = useMemo(() => {
    if (!headerTagsByDsdId || ixdLabelMap.size === 0) return headerTagsByDsdId;
    const out = new Map<string, DsdHeaderTag>();
    for (const [dsdId, tag] of headerTagsByDsdId) {
      const key = tag.lineItem || tag.member;
      const label = key ? ixdLabelMap.get(key) : undefined;
      out.set(dsdId, label ? { ...tag, label } : tag);
    }
    return out;
  }, [headerTagsByDsdId, ixdLabelMap]);

  // 우클릭 데이터 셀 → 백엔드 inferredCellId 로 포커싱. 라이브 화면과 같은 제약을
  // 쓴다(배정된 DL 밖으로 나가지 않는다) — 다운로드본에서만 다르게 굴면 안 된다.
  const handleInferredFocus = navigation.handleInferredCellFocus;

  // 다른 섹션의 연동 위치를 클릭해 이 섹션으로 전환됐다면, 마운트 후 그 셀을 포커스한다.
  // (섹션마다 key 가 달라 마운트 시 1회만 평가된다.)
  //
  // 재시도 정책은 라이브와 같은 것을 쓴다(`focusWithRetry`). 뷰어는 데이터가 임베드라
  // 대개 1회로 성공하지만, 정책이 갈려 있으면 나중에 lazy 렌더가 들어오는 순간 한쪽에서만
  // 경고가 뜬다.
  useEffect(() => {
    const loc = pendingFocus.current;
    if (!loc || !section.ixdChunks.some((c) => c.id === loc.chunkId)) return;
    pendingFocus.current = null;
    return focusWithRetry(
      () => ixdRendererRef.current?.focusCellLocation(loc) ?? false,
      { onGiveUp: () => toast.warning(FOCUS_FAILED_MESSAGE) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // member 검토 축 — 라이브 CompareContentView 와 같은 이유로 밖에서 하나만 고른다
  // (한 셀이 축마다 member 를 갖는데 면색·이름표 채널은 하나).
  const [reviewAxisId, setReviewAxisId] = useState<string | null>(null);
  const memberAxes = useMemo(
    () => buildMemberAxisOptions(section.memberRegions),
    [section.memberRegions],
  );

  // 라이브 CompareContentView 의 패널 영역과 동일한 레이아웃/컴포넌트(캐러셀 포함).
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {regionReview && (
        <div className="flex items-center gap-2 px-5 pt-3">
          <span className="caption-1-normal-medium text-label-assistive">
            추론된 배정 보기
          </span>
          <DsdRegionReviewToggle
            memberAxes={memberAxes}
            reviewAxisId={reviewAxisId}
            onReviewAxisChange={setReviewAxisId}
            betaOnly={false}
          />
        </div>
      )}
      <div className="relative flex gap-5 flex-1 min-h-0 overflow-hidden px-3 p-5">
        <PreviewPanel
          ref={previewPanelRef}
          activeChunk={{
            id: section.id,
            title: section.title,
            subChunks: section.dsdSubChunks,
          }}
          focusedDsdResetKey={focusedDsdResetKey}
          onValueClick={navigation.handleMatchedValueClick}
          onShouldTagCellClick={navigation.handleShouldTagCellClick}
          onNoMatchCellClick={navigation.handleNavigationClose}
          counterpartDsdIds={counterpartDsdIds}
          headerTagsByDsdId={labeledHeaderTags}
          dlRegions={section.dlRegions}
          periodRegions={section.periodRegions}
          lineItemRegions={section.lineItemRegions}
          memberRegions={section.memberRegions}
          reviewAxisId={reviewAxisId}
          ixdLabelMap={ixdLabelMap}
          onInferredFocus={handleInferredFocus}
          onHeaderFocus={navigation.handleHeaderFocus}
          onSelectedDsdCellIdsChange={onDsdCellIdsChange}
        />
        <IxdPreviewPanel
          ixdRendererRef={ixdRendererRef}
          activeChunkId={section.id}
          onClearFocus={handleClearDsdFocus}
          currentIxdChunks={section.ixdChunks}
          activeIxdChunk={activeIxdChunk}
          editable={false}
          cellChanges={EMPTY_CELL_CHANGES}
          cellIdentityByNormalizedCellId={
            identityIndex.identityKeyByNormalizedCellId
          }
          identityChanges={EMPTY_IDENTITY_CHANGES}
          identityLocationsByKey={identityIndex.identityLocationsByKey}
          chunkFsTypeById={EMPTY_FS_TYPE_BY_ID}
          onCellEdit={NOOP}
          onCellEditBatch={NOOP}
          showRawValueInEdit={false}
          onUndo={NOOP}
          onFocusIdentityLocation={onFocusLocation}
          onSelectedCellIdsChange={onIxdCellIdsChange}
          counterpartCellIds={ixdCounterpart.cellIds}
          counterpartIdentityKeys={ixdCounterpart.identityKeys}
          matchedCellIds={matchedIxdCellIds}
          showUnmatchedStatus
        />

        {/* 다중 매칭 시 포커싱된 IXD 셀 옆 네비 팝오버 (라이브와 동일 컴포넌트). */}
        {matchNavigation && matchNavigation.matchingCells.length > 0 && (
          <MatchNavigationPopover
            matchNavigation={matchNavigation}
            onNavigateToIndex={navigation.handleNavigateToIndex}
            onClose={navigation.handleNavigationClose}
          />
        )}
      </div>
    </div>
  );
}
